import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, FileText, Calendar, ArrowLeft, Download,
  AlertTriangle, DollarSign, TrendingUp, TrendingDown
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';

// Move ComparisonRow outside the main component to avoid re-render issues
const ComparisonRow = ({ label, books, gst, isHighlight = false }) => {
  const diff = books - gst;
  const hasMismatch = Math.abs(diff) > 0.01;
  return (
    <tr className={`tl-tr hover:bg-teal-50/40 ${isHighlight ? 'bg-teal-50/50' : ''}`}>
      <td className="tl-td tl-cell-regular">{label}</td>
      <td className="tl-td text-right tl-cell-regular">₹{books.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      <td className="tl-td text-right tl-cell-regular">₹{gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      <td className="tl-td text-right">
        <span className={`text-[13px] font-medium ${hasMismatch ? 'text-rose-600' : 'text-emerald-600'}`}>
          {hasMismatch ? `₹${diff.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
        </span>
      </td>
    </tr>
  );
};

const BooksVsGSTPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [mismatchData, setMismatchData] = useState(null);

  useEffect(() => {
    if (businessId) {
      fetchMismatchReport();
    }
  }, [month, year, businessId]);

  const fetchMismatchReport = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.booksVsGst({ month, year });
      setMismatchData(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const response = await reportsAPI.exportReport({ report_type: 'books-vs-gst', format, month, year });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Books_vs_GST_${month.toString().padStart(2, '0')}_${year}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const summary = mismatchData?.summary || {
    total_mismatches: 0,
    amount_variance: 0,
    tax_variance: 0
  };

  const salesComp = mismatchData?.sales_comparison || {
    total_sales_books: 0,
    total_sales_gstr1: 0,
    taxable_books: 0,
    taxable_gstr1: 0,
    cgst_books: 0,
    cgst_gstr1: 0,
    sgst_books: 0,
    sgst_gstr1: 0,
    igst_books: 0,
    igst_gstr1: 0
  };

  const purchaseComp = mismatchData?.purchase_comparison || {
    total_purchase_books: 0,
    total_purchase_gstr2b: 0,
    itc_books: 0,
    itc_gstr2b: 0
  };

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i).toLocaleString('default', { month: 'long' })
  }));

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const monthLabel = new Date(year, month - 1).toLocaleString('default', { month: 'short', year: 'numeric' });

  return (
    <div className="h-full flex flex-col" data-testid="books-vs-gst-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Books vs GST Mismatch"
        stats={[
          { icon: AlertTriangle, label: 'Total Mismatches', value: summary.total_mismatches, color: 'rose' },
          { icon: DollarSign, label: 'Amount Variance', value: formatCompactCurrency(summary.amount_variance), color: 'amber' },
          { icon: TrendingUp, label: 'Tax Variance', value: formatCompactCurrency(summary.tax_variance), color: 'purple' },
        ]}
        secondaryAction={{
          label: monthLabel,
          icon: Calendar,
          onClick: () => {}
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${businessId}/reports`)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors text-[12px] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                <Calendar className="w-4 h-4 text-slate-400" />
                <select 
                  value={month} 
                  onChange={(e) => setMonth(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select 
                  value={year} 
                  onChange={(e) => setYear(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => handleExport('excel')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Excel
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50">
                <FileText className="w-3.5 h-3.5" />
                PDF
              </button>
              <button onClick={() => handleExport('json')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                <Download className="w-3.5 h-3.5" />
                JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        {loading ? (
          <div className="bg-white rounded-md border border-slate-200/60 shadow-sm flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-[13px] text-slate-500 font-normal">Loading mismatch report...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Sales Comparison */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <h3 className="text-[13px] font-semibold text-slate-700">Sales - Books vs GSTR-1</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '40%' }}>Particulars</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '20%' }}>Books (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '20%' }}>GSTR-1 (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '20%' }}>Difference (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ComparisonRow label="Total Sales Value" books={salesComp.total_sales_books} gst={salesComp.total_sales_gstr1} isHighlight />
                    <ComparisonRow label="Taxable Value" books={salesComp.taxable_books} gst={salesComp.taxable_gstr1} />
                    <ComparisonRow label="CGST" books={salesComp.cgst_books} gst={salesComp.cgst_gstr1} />
                    <ComparisonRow label="SGST" books={salesComp.sgst_books} gst={salesComp.sgst_gstr1} />
                    <ComparisonRow label="IGST" books={salesComp.igst_books} gst={salesComp.igst_gstr1} />
                  </tbody>
                </table>
              </div>
            </div>

            {/* Purchase Comparison */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-purple-500" />
                <h3 className="text-[13px] font-semibold text-slate-700">Purchases - Books vs GSTR-2B</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '40%' }}>Particulars</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '20%' }}>Books (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '20%' }}>GSTR-2B (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '20%' }}>Difference (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ComparisonRow label="Total Purchase Value" books={purchaseComp.total_purchase_books} gst={purchaseComp.total_purchase_gstr2b} isHighlight />
                    <ComparisonRow label="ITC Available" books={purchaseComp.itc_books} gst={purchaseComp.itc_gstr2b} />
                  </tbody>
                </table>
              </div>
            </div>

            {/* Info Alert */}
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[12px] font-medium text-amber-900">Reconciliation Status</p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  Compare your books with filed GST returns to identify mismatches. Ensure all invoices are properly accounted for in both systems.
                  {summary.total_mismatches > 0 && (
                    <span className="font-medium"> {summary.total_mismatches} mismatch(es) found requiring attention.</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BooksVsGSTPage;
